/**
 * Time-zone helpers built on Intl, so they work the same in browsers, Workers and Node.
 * Times are stored in UTC; a company's schedule is reasoned about in its own zone.
 */

const formatters = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatters.set(timeZone, f);
  }
  return f;
}

function zonedParts(date: Date, timeZone: string) {
  const parts: Record<string, number> = {};
  for (const p of partsFormatter(timeZone).formatToParts(date)) {
    if (p.type !== "literal") parts[p.type] = Number(p.value);
  }
  return parts as { year: number; month: number; day: number; hour: number; minute: number; second: number };
}

/** Offset of `timeZone` from UTC at `date`, in milliseconds (positive east of UTC). */
export function zoneOffsetMs(date: Date, timeZone: string): number {
  const p = zonedParts(date, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
}

/**
 * "2026-10-16" + "07:30" in America/New_York -> the UTC instant. Around daylight-saving changes, a
 * time that happens twice (clocks going back) is the first one, and a time that doesn't happen
 * (clocks going forward) moves forward with the clocks: 02:30 on the spring-forward day is 03:30.
 */
export function zonedTimeToUtc(date: string, time: string, timeZone: string): Date {
  const [y, m, d] = date.split("-").map(Number);
  const [hh = 0, mm = 0, ss = 0] = time.split(":").map(Number);
  const wall = Date.UTC(y, m - 1, d, hh, mm, ss);
  // The zone's offsets a day either side; a change, if any, is between them.
  const before = zoneOffsetMs(new Date(wall - 86_400_000), timeZone);
  const after = zoneOffsetMs(new Date(wall + 86_400_000), timeZone);
  const first = wall - before;
  if (zoneOffsetMs(new Date(first), timeZone) === before) return new Date(first);
  const second = wall - after;
  if (zoneOffsetMs(new Date(second), timeZone) === after) return new Date(second);
  // In the gap: read it with the offset from before the change, which lands that much later.
  return new Date(first);
}

/**
 * A time zone name Intl knows, like America/New_York. Not an offset like "+05:00": Postgres reads
 * those as POSIX zones, with the sign the other way round.
 */
export function isTimeZoneName(value: string): boolean {
  if (!/^[A-Za-z][\w+-]*(\/[\w+-]+)*$/.test(value)) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/**
 * zonedTimeToUtc for what's typed in a form: null while the date or time is empty or partial
 * (a cleared date input gives ""), instead of an invalid date that throws when formatted.
 */
export function parseZonedTime(date: string, time: string, timeZone: string): Date | null {
  if (!isIsoDate(date) || !/^\d{2}:\d{2}(:\d{2})?$/.test(time)) return null;
  const instant = zonedTimeToUtc(date, time, timeZone);
  return Number.isNaN(instant.getTime()) ? null : instant;
}

/** The calendar date (YYYY-MM-DD) of an instant in a zone. */
export function localDate(instant: Date, timeZone: string): string {
  const p = zonedParts(instant, timeZone);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

/** Local wall-clock time (HH:MM) of an instant in a zone. */
export function localTime(instant: Date, timeZone: string): string {
  const p = zonedParts(instant, timeZone);
  return `${String(p.hour).padStart(2, "0")}:${String(p.minute).padStart(2, "0")}`;
}

/** Date arithmetic on YYYY-MM-DD strings (no time zone involved). */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Inclusive list of dates between two YYYY-MM-DD strings. */
export function datesBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let day = from; day <= to; day = addDays(day, 1)) out.push(day);
  return out;
}

/** A real YYYY-MM-DD date. Date.parse alone reads 2026-02-30 as March 2, so the date must read back the same. */
export const isIsoDate = (value: unknown): value is string => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const time = Date.parse(value);
  return !Number.isNaN(time) && new Date(time).toISOString().slice(0, 10) === value;
};
